/**
 * useBudgetSettings Hook
 *
 * Manages budget settings with dual persistence:
 * - localStorage for instant access (no auth required)
 * - Firestore for cross-device sync (when authenticated)
 *
 * Settings structure:
 * {
 *   threshold: 10.00,      // USD - budget limit
 *   alertEnabled: true,    // Show alerts when exceeded
 *   updatedAt: Date        // Last modification time
 * }
 *
 * @param {string} userId - Firebase user ID (optional, defaults used if not provided)
 * @returns {Object} - { budget, loading, saveBudget }
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from 'config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const STORAGE_KEY = 'enrichment-budget-threshold';
const DEFAULT_BUDGET = {
  threshold: 10.00,
  alertEnabled: true,
  updatedAt: new Date().toISOString()
};

export const useBudgetSettings = (userId) => {
  // SSR safety check
  const isClient = typeof window !== 'undefined';

  // Initialize from localStorage immediately for instant access
  const [budget, setBudget] = useState(() => {
    if (!isClient) {
      return DEFAULT_BUDGET;
    }

    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate structure
        if (parsed &&
            typeof parsed.threshold === 'number' &&
            typeof parsed.alertEnabled === 'boolean') {
          return parsed;
        }
        console.warn('[useBudgetSettings] Invalid stored data, using defaults');
      }
    } catch (error) {
      console.warn('[useBudgetSettings] Failed to parse localStorage', error);
    }

    return DEFAULT_BUDGET;
  });

  const [loading, setLoading] = useState(false);

  // Load from Firestore on mount (when userId available)
  useEffect(() => {
    if (!userId || !isClient) {
      return;
    }

    const loadFromFirestore = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'settings', 'enrichment', 'budgets', userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          // Validate Firestore data
          if (data &&
              typeof data.threshold === 'number' &&
              typeof data.alertEnabled === 'boolean') {
            const firestoreData = {
              threshold: data.threshold,
              alertEnabled: data.alertEnabled,
              updatedAt: data.updatedAt || new Date().toISOString()
            };

            // Update state and sync to localStorage
            setBudget(firestoreData);
            try {
              window.localStorage.setItem(STORAGE_KEY, JSON.stringify(firestoreData));
            } catch (storageError) {
              console.warn('[useBudgetSettings] Failed to sync to localStorage', storageError);
            }
          }
        }
      } catch (error) {
        console.error('[useBudgetSettings] Failed to load from Firestore', error);
        // Continue with localStorage/default values on error
      } finally {
        setLoading(false);
      }
    };

    loadFromFirestore();
  }, [userId, isClient]);

  // Save function writes to both Firestore and localStorage
  const saveBudget = useCallback(async (newSettings) => {
    if (!isClient) {
      return { success: false, error: 'Not in browser environment' };
    }

    // Validate input
    if (!newSettings ||
        typeof newSettings.threshold !== 'number' ||
        typeof newSettings.alertEnabled !== 'boolean') {
      return { success: false, error: 'Invalid settings structure' };
    }

    if (newSettings.threshold < 0) {
      return { success: false, error: 'Threshold must be non-negative' };
    }

    const settingsToSave = {
      ...newSettings,
      updatedAt: new Date().toISOString()
    };

    try {
      // Save to localStorage immediately
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
      setBudget(settingsToSave);

      // Save to Firestore if authenticated
      if (userId) {
        const docRef = doc(db, 'settings', 'enrichment', 'budgets', userId);
        await setDoc(docRef, settingsToSave, { merge: true });
      }

      return { success: true };
    } catch (error) {
      console.error('[useBudgetSettings] Failed to save budget', error);

      // Handle quota exceeded for localStorage
      if (error.name === 'QuotaExceededError') {
        try {
          // Clear old enrichment data to make space
          Object.keys(window.localStorage)
            .filter(k => k.startsWith('enrichment-'))
            .slice(0, 5)
            .forEach(k => window.localStorage.removeItem(k));

          // Retry save
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
          setBudget(settingsToSave);

          // Still try Firestore
          if (userId) {
            const docRef = doc(db, 'settings', 'enrichment', 'budgets', userId);
            await setDoc(docRef, settingsToSave, { merge: true });
          }

          return { success: true };
        } catch (retryError) {
          return { success: false, error: 'Storage quota exceeded' };
        }
      }

      return { success: false, error: error.message };
    }
  }, [userId, isClient]);

  return {
    budget,
    loading,
    saveBudget
  };
};

export default useBudgetSettings;
